/** Sprint 5-c · 公司語音會議室 API client（對接 backend #276 · /api/ee/office-meetings）。 */
import axios from '../../../../api/axios';

const BASE = '/api/ee/office-meetings';

export interface MeetingAttendee {
  user_id: number;
  name: string;
}

export interface OfficeMeeting {
  id: number;
  org_id: number;
  meeting_no: string;
  title: string;
  description?: string | null;
  location?: string | null;
  start_at: string;
  end_at: string;
  owner_user_id: number;
  owner_name: string;
  chair_user_id?: number | null;
  chair_name?: string | null;
  recorder_user_id?: number | null;
  recorder_name?: string | null;
  can_edit_minutes?: boolean;   // 目前使用者是否可編輯會議紀錄（主席/紀錄人員/建立者）
  recording_mode?: string | null;      // room（實體會議室單裝置）/ channel（未來線上）
  recording_user_id?: number | null;   // 錄音裝置使用者
  recording_user_name?: string | null;
  is_chair?: boolean;                   // 目前使用者是否為主席（可開始/結束）
  can_record?: boolean;                 // 目前使用者是否為錄音裝置
  status: 'scheduled' | 'in_progress' | 'ended' | 'cancelled';
  public_meeting?: boolean;             // false=秘密會議（Stage 3）
  calendar_event_id?: number | null;   // 推導來源的主辦人日曆事件
  recording_started_at?: string | null;
  recording_ended_at?: string | null;
  created_at: string;
  attendees?: MeetingAttendee[];
}

export interface MinutesListItem {
  id: number;
  meeting_no: string;
  title: string;
  start_at: string;
  end_at: string;
  chair_name?: string | null;
  recorder_name?: string | null;
  summary: string;
  can_edit_minutes: boolean;
}

export interface LiveAttendee {
  user_id: number;
  name: string;
  joined_at: string;
  left_at?: string | null;
  present: boolean;
}

export interface ActionItem {
  id: string;
  content: string;
  assignee_user_id?: number | null;
  assignee_name?: string;
  due?: string;
  status?: string;
}

export interface Minutes {
  meeting_id: number;
  full_transcript: string;
  summary?: string | null;
  decisions: { content: string }[];
  action_items: ActionItem[];
  attendees_actual: { user_id: number; name: string; joined_at?: string | null; left_at?: string | null }[];
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_ntd: number;
  email_sent_at?: string | null;
  email_recipients: string[];
}

export interface MeetingCreateBody {
  title: string;
  start_at: string;
  end_at: string;
  description?: string;
  location?: string;
  attendee_user_ids: number[];
  chair_user_id?: number | null;
  recorder_user_id?: number | null;
}

export interface MeetingUpdateBody {
  title?: string;
  start_at?: string;
  end_at?: string;
  description?: string;
  location?: string;
  chair_user_id?: number | null;
  recorder_user_id?: number | null;
}

export const listMeetings = async (): Promise<OfficeMeeting[]> =>
  (await axios.get<OfficeMeeting[]>(BASE)).data;

export const createMeeting = async (body: MeetingCreateBody): Promise<OfficeMeeting> =>
  (await axios.post<OfficeMeeting>(BASE, body)).data;

export const getMeeting = async (id: number): Promise<OfficeMeeting> =>
  (await axios.get<OfficeMeeting>(`${BASE}/${id}`)).data;

export const updateMeeting = async (id: number, body: MeetingUpdateBody): Promise<OfficeMeeting> =>
  (await axios.put<OfficeMeeting>(`${BASE}/${id}`, body)).data;

export const cancelMeeting = async (id: number): Promise<void> => {
  await axios.delete(`${BASE}/${id}`);
};

export const listAttendees = async (id: number): Promise<MeetingAttendee[]> =>
  (await axios.get<MeetingAttendee[]>(`${BASE}/${id}/attendees`)).data;

export const addAttendee = async (id: number, userId: number): Promise<MeetingAttendee[]> =>
  (await axios.post<MeetingAttendee[]>(`${BASE}/${id}/attendees`, { user_id: userId })).data;

export const removeAttendee = async (id: number, userId: number): Promise<void> => {
  await axios.delete(`${BASE}/${id}/attendees/${userId}`);
};

export const checkIn = async (id: number, deviceInfo?: string): Promise<void> => {
  await axios.post(`${BASE}/${id}/check-in`, { device_info: deviceInfo });
};

export const checkOut = async (id: number): Promise<void> => {
  await axios.post(`${BASE}/${id}/check-out`, {});
};

export const liveAttendees = async (id: number): Promise<LiveAttendee[]> =>
  (await axios.get<LiveAttendee[]>(`${BASE}/${id}/live-attendees`)).data;

export const startRecording = async (
  id: number, recorderUserId?: number | null,
): Promise<{ status: string; recording_started_at: string; recording_user_id?: number; recording_user_name?: string }> =>
  (await axios.post(`${BASE}/${id}/start-recording`,
    recorderUserId != null ? { recorder_user_id: recorderUserId } : {})).data;

export const addChunk = async (
  id: number, chunkIndex: number, transcript: string, durationMs: number,
): Promise<{ meeting_id: number; chunk_count: number }> =>
  (await axios.post(`${BASE}/${id}/chunk`,
    { chunk_index: chunkIndex, transcript, duration_ms: durationMs })).data;

export const stopRecording = async (id: number): Promise<Minutes> =>
  (await axios.post<Minutes>(`${BASE}/${id}/stop-recording`, {})).data;

export const listMyMinutes = async (): Promise<MinutesListItem[]> =>
  (await axios.get<MinutesListItem[]>(`${BASE}/my-minutes`)).data;

export const getMinutes = async (id: number): Promise<Minutes> =>
  (await axios.get<Minutes>(`${BASE}/${id}/minutes`)).data;

export const updateMinutes = async (
  id: number, body: { summary?: string; decisions?: { content: string }[]; action_items?: ActionItem[] },
): Promise<Minutes> =>
  (await axios.put<Minutes>(`${BASE}/${id}/minutes`, body)).data;

export const estimateCost = async (minutes: number): Promise<{ estimated_ntd: number }> =>
  (await axios.post(`${BASE}/estimate-cost`, { estimated_duration_min: minutes })).data;
